import * as Sentry from "@sentry/react";
import queryString from "qs";

// 500번대 에러 타입 정의
const SERVER_ERROR_TYPES = {
  500: { name: "internal_server_error", description: "Internal Server Error" },
  501: { name: "not_implemented", description: "Not Implemented" },
  502: { name: "bad_gateway", description: "Bad Gateway" },
  503: { name: "service_unavailable", description: "Service Unavailable" },
  504: { name: "gateway_timeout", description: "Gateway Timeout" },
  505: {
    name: "http_version_not_supported",
    description: "HTTP Version Not Supported",
  },
  506: {
    name: "variant_also_negotiates",
    description: "Variant Also Negotiates",
  },
  507: { name: "insufficient_storage", description: "Insufficient Storage" },
  508: { name: "loop_detected", description: "Loop Detected" },
  509: {
    name: "bandwidth_limit_exceeded",
    description: "Bandwidth Limit Exceeded",
  },
  510: { name: "not_extended", description: "Not Extended" },
  511: {
    name: "network_authentication_required",
    description: "Network Authentication Required",
  },
};

let REACT_APP_VERSION_TYPE = process.env.REACT_APP_VERSION_TYPE;

// Sentry 에러 캡처 함수
const captureSentryError = (
  error,
  endpoint,
  method,
  additionalContext = {},
) => {
  const statusCode = error.status || error.statusCode;

  if (statusCode >= 500 && statusCode <= 599) {
    const errorType = SERVER_ERROR_TYPES[statusCode] || {
      name: `server_error_${statusCode}`,
      description: `Server Error ${statusCode}`,
    };

    Sentry.captureException(error, {
      tags: {
        error_category: "5xx_error",
        error_type: errorType.name,
        http_status: statusCode.toString(),
        http_status_group: "5xx",
        api_endpoint: endpoint,
        api_method: method,
        api_version: REACT_APP_VERSION_TYPE,
      },
      contexts: {
        server_error_details: {
          status_code: statusCode,
          error_name: errorType.name,
          error_description: errorType.description,
          is_timeout: statusCode === 504,
          is_unavailable: statusCode === 503,
        },
        api_request: {
          url: `${process.env.REACT_APP_API_URL}${endpoint}`,
          method: method,
          timestamp: new Date().toISOString(),
          ...additionalContext,
        },
      },
      level: statusCode === 503 || statusCode === 504 ? "warning" : "error",
    });
  }
};

// const parseObjToBodyString = (obj) => {
//   return Object.keys(obj)
//     .map((key) => `${key}=${obj[key]}`)
//     .join("&");
// };

const parseResponseJson = async (res) => {
  const text = await res.text();
  let parsed = {};

  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch (_e) {
      parsed = {};
    }
  }

  return parsed;
};

const api = {};

api.get = (endpoint, query = {}) => {
  let isJson = true;

  const URL = `${
    process.env.REACT_APP_API_URL
  }${endpoint}?${queryString.stringify(query)}`;
  return fetch(URL, {
    credentials: "include",
    method: "GET",
    mode: "cors",
  })
    .then((res) => {
      var contentType = res.headers.get("content-type");
      if (contentType.includes("openxmlformats")) {
        isJson = false;
        return res.blob();
      } else {
        return res.json();
      }
    })
    .then((res) => {
      if (!isJson) {
        return res;
      }

      if (res.statusCode >= 300) {
        const error = new Error();
        error.status = res.statusCode;
        error.statusCode = res.statusCode;
        error.message = res.error || res.message;

        // Sentry 에러 캡처
        captureSentryError(error, endpoint, "GET", { query });

        throw error;
      }

      return res;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "GET",
          },
        });
      }
      throw error;
    });
};

api.post = (endpoint, body = {}, timeoutMs = 1800000) => {
  // 기본값 30분 (1800,000ms)
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;

  // AbortController 생성
  const controller = new AbortController();
  const signal = controller.signal;

  // 타임아웃 설정
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "post",
    mode: "cors",
    signal: signal, // AbortController의 signal 추가
  })
    .then(async (res) => {
      clearTimeout(timeoutId);
      const parsed = await parseResponseJson(res);
      const statusCode = parsed.statusCode || (!res.ok ? res.status : 200);

      if (statusCode >= 300) {
        const error = new Error();
        error.status = statusCode;
        error.statusCode = statusCode;
        error.message = parsed.error || parsed.message || res.statusText;

        captureSentryError(error, endpoint, "POST", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return parsed;
    })
    .catch((error) => {
      clearTimeout(timeoutId); // 에러 발생 시에도 타임아웃 제거

      // AbortError(타임아웃으로 인한 중단) 처리
      if (error.name === "AbortError") {
        const timeoutMinutes = Math.floor(timeoutMs / 60000);
        const timeoutError = new Error(
          `요청 시간이 초과되었습니다 (${timeoutMinutes}분)`,
        );
        timeoutError.status = 408; // Request Timeout
        timeoutError.statusCode = 408;
        throw timeoutError;
      }

      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "POST",
          },
        });
      }

      throw error;
    });
};

api.postForDownload = (endpoint, body = {}) => {
  let isJson = true;

  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "post",
    mode: "cors",
  })
    .then((res) => {
      var contentType = res.headers.get("content-type");
      if (contentType.includes("openxmlformats")) {
        isJson = false;
        return res.blob();
      } else {
        return res.json();
      }
    })
    .then((res) => {
      if (!isJson) {
        return res;
      }

      if (res.statusCode >= 300) {
        const error = new Error();
        error.status = res.statusCode;
        error.statusCode = res.statusCode;
        error.message = res.error || res.message;

        // Sentry 에러 캡처
        captureSentryError(error, endpoint, "POST", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return res;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "POST",
          },
        });
      }
      throw error;
    });
};

api.put = (endpoint, body = {}) => {
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "put",
    mode: "cors",
  })
    .then(async (res) => {
      const parsed = await parseResponseJson(res);
      const statusCode = parsed.statusCode || (!res.ok ? res.status : 200);

      if (statusCode >= 300) {
        const error = new Error();
        error.status = statusCode;
        error.statusCode = statusCode;
        error.message = parsed.error || parsed.message || res.statusText;

        captureSentryError(error, endpoint, "PUT", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return parsed;
    })
    .catch((error) => {
      // 네트워크 에러 등도 캡처
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "PUT",
          },
        });
      }
      throw error;
    });
};

api.patch = (endpoint, body = {}) => {
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "PATCH",
    mode: "cors",
  })
    .then(async (res) => {
      const parsed = await parseResponseJson(res);
      const statusCode = parsed.statusCode || (!res.ok ? res.status : 200);

      if (statusCode >= 300) {
        const error = new Error();
        error.status = statusCode;
        error.statusCode = statusCode;
        error.message = parsed.error || parsed.message || res.statusText;

        captureSentryError(error, endpoint, "PATCH", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return parsed;
    })
    .catch((error) => {
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "PATCH",
          },
        });
      }
      throw error;
    });
};

api.delete = (endpoint, body = {}) => {
  const URL = `${process.env.REACT_APP_API_URL}${endpoint}`;
  return fetch(URL, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "delete",
    mode: "cors",
  })
    .then(async (res) => {
      const parsed = await parseResponseJson(res);
      const statusCode = parsed.statusCode || (!res.ok ? res.status : 200);

      if (statusCode >= 300) {
        const error = new Error();
        error.status = statusCode;
        error.statusCode = statusCode;
        error.message = parsed.error || parsed.message || res.statusText;

        captureSentryError(error, endpoint, "DELETE", {
          body_size: JSON.stringify(body).length,
          has_body: Object.keys(body).length > 0,
        });

        throw error;
      }

      return parsed;
    })
    .catch((error) => {
      if (!error.status && !error.statusCode) {
        Sentry.captureException(error, {
          tags: {
            error_category: "network_error",
            error_type: "connection_failed",
            api_endpoint: endpoint,
            api_method: "DELETE",
          },
        });
      }
      throw error;
    });
};

export default api;
